// Team invitations held back until the owner has secured their account.
//
// With MFA_ENFORCEMENT_MODE=before_regulated_action the API refuses
// `POST /team/invitations` (403 AU403MFAA) for an account with no second
// factor. A new institution owner adds teammates during onboarding, before they
// could have enrolled, so those invitations used to be refused and lost.
//
// They are kept here instead, keyed by user id, and sent once enrolment shows
// in `/auth/mfa/status`.
//
// THIS IS NOT THE CONTROL. Holding an invitation sends nothing and grants
// nothing. When it is released it goes through the same guarded endpoint as any
// other invitation, so the server still decides, and still refuses, whether or
// not this file thinks the user is enrolled. The queue is browser-local on
// purpose: a server-side pending state would need an unguarded way to create it
// (an MFA exemption for invitations) or a migration, and neither is needed to
// keep a handful of typed email addresses.

import { sendInvitation } from './api';
import { isMfaRequiredForAction } from '@/features/Auth/mfaReturn';

export type QueuedRole = 'admin' | 'member' | 'publisher';

export interface QueuedInvite {
  email: string;
  orgRole: QueuedRole;
}

const KEY_PREFIX = 'biasafe.pendingInvites.v1.';
/** Fired in this tab after any change; `storage` covers the other tabs. */
export const PENDING_INVITES_EVENT = 'biasafe:pending-invites';

const keyFor = (userId: string) => `${KEY_PREFIX}${userId}`;

function normalise(email: string): string {
  return email.trim().toLowerCase();
}

export function readPendingInvites(
  userId: string | null | undefined,
): Array<QueuedInvite> {
  if (!userId) return [];
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (i): i is QueuedInvite =>
        typeof i === 'object' &&
        i !== null &&
        typeof (i as QueuedInvite).email === 'string' &&
        ['admin', 'member', 'publisher'].includes((i as QueuedInvite).orgRole),
    );
  } catch {
    return [];
  }
}

function write(userId: string, invites: Array<QueuedInvite>): void {
  try {
    if (invites.length === 0) {
      window.localStorage.removeItem(keyFor(userId));
    } else {
      window.localStorage.setItem(keyFor(userId), JSON.stringify(invites));
    }
    window.dispatchEvent(new Event(PENDING_INVITES_EVENT));
  } catch {
    // Storage blocked or full: the queue degrades to "not held", which is the
    // old behaviour (the caller already told the user to resend from Team).
  }
}

/** Hold invitations. A repeated address replaces its earlier entry. */
export function queueInvites(
  userId: string,
  invites: Array<QueuedInvite>,
): Array<QueuedInvite> {
  const byEmail = new Map<string, QueuedInvite>();
  for (const i of readPendingInvites(userId)) byEmail.set(normalise(i.email), i);
  for (const i of invites) {
    byEmail.set(normalise(i.email), {
      email: normalise(i.email),
      orgRole: i.orgRole,
    });
  }
  const next = [...byEmail.values()];
  write(userId, next);
  return next;
}

export function removePendingInvite(userId: string, email: string): void {
  const target = normalise(email);
  write(
    userId,
    readPendingInvites(userId).filter((i) => normalise(i.email) !== target),
  );
}

export interface ReleaseResult {
  sent: number;
  /** Refused for a reason retrying will not fix (bad address, seat limit...). */
  failed: number;
  /** Still held: refused for lack of a second factor, or the API was unreachable. */
  held: number;
}

let releasing: Promise<ReleaseResult> | null = null;

function isPermanent(err: unknown): boolean {
  const status = (err as { response?: { status?: number } } | null)?.response
    ?.status;
  // No response (network), 5xx and 429 are worth another attempt.
  return (
    typeof status === 'number' && status >= 400 && status < 500 && status !== 429
  );
}

/**
 * Send every held invitation through the guarded endpoint.
 *
 * Idempotent. Each entry is taken out of storage BEFORE its request, so a
 * second tab or a re-render that calls this concurrently finds nothing left to
 * send; an entry goes back only if its request did not complete. Within a tab,
 * concurrent calls share one run. Should a send nonetheless repeat, the
 * endpoint refreshes the existing pending invitation for the same address
 * rather than creating another.
 */
export function releasePendingInvites(userId: string): Promise<ReleaseResult> {
  if (releasing) return releasing;
  releasing = (async () => {
    const result: ReleaseResult = { sent: 0, failed: 0, held: 0 };
    const queue = readPendingInvites(userId);
    for (let idx = 0; idx < queue.length; idx++) {
      const invite = queue[idx];
      const stillHeld = readPendingInvites(userId);
      if (
        !stillHeld.some((i) => normalise(i.email) === normalise(invite.email))
      ) {
        continue; // another tab took it, or the user removed it
      }
      removePendingInvite(userId, invite.email);
      try {
        await sendInvitation({
          email: normalise(invite.email),
          orgRole: invite.orgRole,
        });
        result.sent += 1;
      } catch (err) {
        if (isMfaRequiredForAction(err) || !isPermanent(err)) {
          // Not sent: keep this one and everything after it for next time.
          queueInvites(userId, queue.slice(idx));
          result.held += queue.length - idx;
          break;
        }
        result.failed += 1;
      }
    }
    return result;
  })().finally(() => {
    releasing = null;
  });
  return releasing;
}
